import React, { useCallback, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { formValueSelector } from "redux-form";

import {
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectFormGroup,
} from "components";
import {
	AriseFeesFields,
	DebounceTimeout,
	GatewayFeesFields,
} from "globalConstants";
import { isEmptyObject } from "helpers";
import { useBackendPlatform } from "hooks";
import { useGetGatewayNamesQuery } from "queries";
import {
	GatewayConfigurationOption,
	TerminalFileBuildOptionTypes,
	TerminalGatewayTypes,
} from "types";
import {
	float6Normalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import {
	currencySixSigns,
	floatToSixValidation,
	maxLength100,
	required,
} from "validators/validation";

import BillSection from "./billSection";
import RiseOsSection from "./riseOsSection";
import Tokenization from "./tokenization";
import Transarmor from "./transarmor";

import styles from "../terminalSolution.module.scss";

type GatewayProps = {
	sectionName?: string;
	isLoading: boolean;
	merchantInfo: any;
	merchantFees?: { [key: string]: any };
	backEndPlatformType: string;
	isApplication?: boolean;
	fileBuildSectionName: string;
	feesFormName?: string;
};

const Gateway = ({
	sectionName = "gateway",
	isLoading = false,
	merchantInfo,
	merchantFees,
	backEndPlatformType,
	isApplication = false,
	fileBuildSectionName,
	feesFormName = "",
}: GatewayProps) => {
	const {
		register,
		watch,
		setValue,

		formState: { errors },
	} = useFormContext();

	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
	} = useBackendPlatform(backEndPlatformType);

	const getFiledName = (fieldName: string) => `${sectionName}.${fieldName}`;

	const FeesFieldsNames = {
		MonthlyFee: getFiledName(GatewayFeesFields.MonthlyFee.name),
		TransactionFee: getFiledName(GatewayFeesFields.TransactionFee.name),
		Level3PerTransactionFee: getFiledName(
			GatewayFeesFields.Level3PerTransactionFee.name,
		),
		SetupFee: getFiledName(GatewayFeesFields.SetupFee.name),
		VaultMonthlyFee: getFiledName(GatewayFeesFields.VaultMonthlyFee.name),
	};

	const reduxFormSelector = feesFormName
		? formValueSelector(feesFormName)
		: () => ({});
	const ariseFeesValues = useSelector(state =>
		reduxFormSelector(
			state,
			AriseFeesFields.PlatformMonthlyFee.name,
			AriseFeesFields.CardTransactionAuthorizationFee.name,
			AriseFeesFields.EnhancedDataTransactionFee.name,
			AriseFeesFields.PlatformSetupFee.name,
		),
	);

	const feesValues = merchantFees || ariseFeesValues;

	const { data: gateways } = useGetGatewayNamesQuery();

	const filteredGateways = gateways?.filter(({ name }) => {
		return isBackEndPlatformOmaha || isBackEndPlatformNorth
			? true
			: name !== "Payeezy";
	});

	const isTsysTokenizationEnabled = watch(
		`${sectionName}.isTsysTokenizationEnabled`,
	);
	const gatewayName = watch(`${sectionName}.gatewayName`);

	const isOtherGateway =
		gatewayName?.name === TerminalGatewayTypes.Other.name;
	const isPayeezyGateway =
		gatewayName?.name === TerminalGatewayTypes.Payeezy.name;
	const isRiseOsGateway =
		gatewayName?.name === TerminalGatewayTypes.RiseOS.name;
	const isAriseGateway =
		gatewayName?.name === TerminalGatewayTypes.ARISE.name;

	const showCardNetworkTokenization =
		!isApplication &&
		isAriseGateway &&
		isTsysTokenizationEnabled === "true";

	const showCardNetworkTokenizationRadio =
		isBackEndPlatformTsys && (!isApplication || !isAriseGateway);

	const showCustomerVaultRadio = !isAriseGateway || !isApplication;

	const hideAriseCustomerVaultFees =
		isAriseGateway && isEmptyObject(feesValues);

	useEffect(() => {
		if (isPayeezyGateway) {
			setValue(`${sectionName}.isTransarmorEnabled`, "true");
		}
	}, [isPayeezyGateway, sectionName, setValue]);

	useEffect(() => {
		if (isAriseGateway) {
			setValue(
				FeesFieldsNames.MonthlyFee,
				feesValues[AriseFeesFields.PlatformMonthlyFee.name] ||
					"0.000000",
			);
			setValue(
				FeesFieldsNames.TransactionFee,
				feesValues[
					AriseFeesFields.CardTransactionAuthorizationFee.name
				] || "0.000000",
			);
			setValue(
				FeesFieldsNames.Level3PerTransactionFee,
				feesValues[AriseFeesFields.EnhancedDataTransactionFee.name] ||
					"0.000000",
			);
			setValue(
				FeesFieldsNames.SetupFee,
				feesValues[AriseFeesFields.PlatformSetupFee.name] || "0.000000",
			);
		}
	}, [
		isAriseGateway,
		FeesFieldsNames.Level3PerTransactionFee,
		FeesFieldsNames.MonthlyFee,
		FeesFieldsNames.SetupFee,
		FeesFieldsNames.TransactionFee,
		feesValues,
		setValue,
	]);

	const handleGatewayNameChange = useCallback(
		gateway => {
			if (gateway?.name !== TerminalGatewayTypes.Payeezy.name) {
				setValue(`${sectionName}.isTransarmorEnabled`, "false");
			}
			if (
				isBackEndPlatformTsys &&
				gateway?.name === TerminalGatewayTypes.ARISE.name
			) {
				setValue(
					`${fileBuildSectionName}.fileBuildOption`,
					TerminalFileBuildOptionTypes.TsysTransit.name,
				);
			}
			if (gateway?.name === TerminalGatewayTypes.ARISE.name) {
				setValue(
					`${sectionName}.isCustomerVaultOrRecurringBilling`,
					"true",
				);
			}
		},
		[fileBuildSectionName, isBackEndPlatformTsys, sectionName, setValue],
	);

	return (
		<div data-autotest="gatewaySection">
			<Row className="form-group">
				<HookFormRadioButton
					id="gatewayTypeNew"
					register={register}
					name={`${sectionName}.gatewayConfigurationOption`}
					value={GatewayConfigurationOption.NewGateway.name}
					label={GatewayConfigurationOption.NewGateway.displayName}
					blurLoading={isLoading}
					className="col-md-2"
				/>
				<HookFormRadioButton
					id="gatewayTypeFileBuild"
					register={register}
					name={`${sectionName}.gatewayConfigurationOption`}
					value={GatewayConfigurationOption.FileBuildOnly.name}
					label={GatewayConfigurationOption.FileBuildOnly.displayName}
					blurLoading={isLoading}
					className="col-md-2"
				/>
			</Row>
			<Row className="form-group">
				<Col md={5}>
					<HookSelectFormGroup
						label="Name"
						options={filteredGateways}
						getOptionLabel={option => option.name}
						getOptionValue={option => option.id}
						placeholder="Please Select"
						name={`${sectionName}.gatewayName`}
						isSearchable
						cacheOptions
						defaultOptions
						defaultValue={null}
						debounceTimeout={DebounceTimeout}
						blurLoading={isLoading}
						onChange={handleGatewayNameChange}
						rules={{
							validate: {
								required,
							},
						}}
					/>
					{isOtherGateway && (
						<HookFormInputGroup
							name={`${sectionName}.otherGatewayName`}
							register={register}
							label="Gateway Name"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							validator={{
								maxLength100,
								required,
							}}
							normalize={trimMidSentenceNormalizer}
							showRequiredMark
							normalizeOnBlur={trimNormalizer}
							errors={errors}
						/>
					)}
				</Col>
				{!isApplication && (
					<Col md={{ span: 5, offset: 2 }}>
						<HookFormInputGroup
							name={`${sectionName}.gatewayId`}
							register={register}
							label="Gateway ID"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							validator={{
								maxLength100,
								required,
							}}
							normalize={trimMidSentenceNormalizer}
							normalizeOnBlur={trimNormalizer}
							errors={errors}
							showRequiredMark
						/>
					</Col>
				)}
			</Row>
			{showCustomerVaultRadio && (
				<>
					<p>Customer Vault / Recurring Billing</p>
					<Row className="form-group">
						<Col md={2}>
							<HookFormRadioButton
								id={`${sectionName}.recurringBillingYes`}
								register={register}
								name={`${sectionName}.isCustomerVaultOrRecurringBilling`}
								value="true"
								label="Yes"
								blurLoading={isLoading}
							/>
						</Col>
						<Col md={2}>
							<HookFormRadioButton
								id={`${sectionName}.recurringBillingNo`}
								register={register}
								name={`${sectionName}.isCustomerVaultOrRecurringBilling`}
								value="false"
								label="No"
								blurLoading={isLoading}
								disabled={isAriseGateway}
							/>
						</Col>
					</Row>
				</>
			)}
			{!hideAriseCustomerVaultFees && (
				<>
					<Row className="form-group">
						<Col md={5}>
							<HookFormInputGroup
								name={FeesFieldsNames.MonthlyFee}
								register={register}
								label={GatewayFeesFields.MonthlyFee.displayName}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								showRequiredMark={!isAriseGateway}
								placeholder="0.000000"
								validator={{ floatToSixValidation, required }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
								isPlainText={isAriseGateway}
							/>
						</Col>
						<Col md={{ span: 5, offset: 2 }}>
							<HookFormInputGroup
								name={FeesFieldsNames.TransactionFee}
								register={register}
								label={
									GatewayFeesFields.TransactionFee.displayName
								}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								showRequiredMark={!isAriseGateway}
								placeholder="0.000000"
								validator={{ floatToSixValidation, required }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
								isPlainText={isAriseGateway}
							/>
						</Col>
					</Row>
					<Row className="form-group">
						<Col md={5}>
							<HookFormInputGroup
								name={FeesFieldsNames.Level3PerTransactionFee}
								register={register}
								label={
									GatewayFeesFields.Level3PerTransactionFee
										.displayName
								}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								showRequiredMark={!isAriseGateway}
								placeholder="0.000000"
								validator={{ floatToSixValidation, required }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
								labelClassName="pr-0"
								isPlainText={isAriseGateway}
							/>
						</Col>
						<Col md={{ span: 5, offset: 2 }}>
							<HookFormInputGroup
								name={FeesFieldsNames.SetupFee}
								register={register}
								label={GatewayFeesFields.SetupFee.displayName}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								placeholder="0.000000"
								validator={{ floatToSixValidation }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
								isPlainText={isAriseGateway}
							/>
						</Col>
					</Row>
				</>
			)}
			{!isAriseGateway && (
				<Row className="form-group">
					<Col md={5}>
						<HookFormInputGroup
							name={FeesFieldsNames.VaultMonthlyFee}
							register={register}
							label={
								GatewayFeesFields.VaultMonthlyFee.displayName
							}
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							showRequiredMark
							placeholder="0.000000"
							validator={{ floatToSixValidation, required }}
							normalize={currencySixSigns}
							normalizeOnBlur={float6Normalizer}
							errors={errors}
						/>
					</Col>
				</Row>
			)}
			{isRiseOsGateway && (
				<RiseOsSection
					sectionName={sectionName}
					isLoading={isLoading}
					isApplication={isApplication}
				/>
			)}
			{isBackEndPlatformOmaha || isBackEndPlatformNorth ? (
				<Transarmor
					sectionName={sectionName}
					isLoading={isLoading}
					disabled={isPayeezyGateway}
				/>
			) : showCardNetworkTokenizationRadio ? (
				<Tokenization sectionName={sectionName} isLoading={isLoading} />
			) : null}
			{showCardNetworkTokenization && (
				<>
					<div className={styles.subHeader}>
						Card Network Tokenization Service Fees
					</div>
					<Row className="form-group">
						<Col md={5}>
							<HookFormInputGroup
								name={`${sectionName}.${AriseFeesFields.CardNetworkTokenizationSetupFee.name}`}
								register={register}
								label={`${AriseFeesFields.CardNetworkTokenizationSetupFee.displayFullName}, $`}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								showRequiredMark
								placeholder="0.000000"
								validator={{ floatToSixValidation, required }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
								labelClassName="pr-0"
							/>
						</Col>
						<Col md={{ span: 5, offset: 2 }}>
							<HookFormInputGroup
								name={`${sectionName}.${AriseFeesFields.CardNetworkTokenizationMonthlyFee.name}`}
								register={register}
								label={`${AriseFeesFields.CardNetworkTokenizationMonthlyFee.displayFullName}, $`}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								showRequiredMark
								placeholder="0.000000"
								validator={{ floatToSixValidation, required }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
							/>
						</Col>
					</Row>
					<Row className="form-group">
						<Col md={5}>
							<HookFormInputGroup
								name={`${sectionName}.${AriseFeesFields.CardNetworkPerTokenizationEventFee.name}`}
								register={register}
								label={`${AriseFeesFields.CardNetworkPerTokenizationEventFee.displayFullName}, $`}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								showRequiredMark
								placeholder="0.000000"
								validator={{ floatToSixValidation, required }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
							/>
						</Col>
					</Row>
				</>
			)}
			{!isApplication && (
				<BillSection
					isLoading={isLoading}
					merchantInfo={merchantInfo}
				/>
			)}
		</div>
	);
};

export default Gateway;
